// src/modules/surgeon/EditSurgeon/EditSurgeon.tsx
import React, {useEffect, useState} from 'react';
import './EditSurgeon.scss';
import PersonIcon from '@material-ui/icons/Person';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import DeleteIcon from '../../../assets/images/trash.png';
import CloseIcon from '@material-ui/icons/Close';
import {
  deleteSurgeon,
  getSurgeonById,
  getSurgeonProfilePicture,
  updateSurgeonPreferences,
  updateSurgeonProfilePic,
} from '../../../../../shared/src/services/SurgeonService';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {_snackbarAcknowledgement} from '../../../../../shared/src/store/actions/AuthActions';

interface Surgeon {
  id: string;
  name: string;
  surgeon_id: string;
  service_line_name: string;
  profilePic: string;
  preferences: string;
  profilePicFile: any;
}

interface EditSurgeonProps {
  surgeonId: string;
  onClose: (refresh?: boolean) => void;
  onSave?: () => void;
  open: boolean;
}

const EditSurgeon: React.FC<EditSurgeonProps> = ({
  surgeonId,
  onClose,
  onSave,
  open,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [surgeon, setSurgeon] = useState<Surgeon | null>(null);
  const [preferences, setPreferences] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(
    false
  );
  const [editorRef, setEditorRef] = useState<HTMLDivElement | null>(null);
  const [deleteProfilePicture, setDeleteProfilePicture] = useState<boolean>(
    false
  );

  useEffect(() => {
    const loadSurgeon = async () => {
      if (!surgeonId) return;
      try {
        setIsLoading(true);
        const result = await getSurgeonById(surgeonId);
        const {data} = result;
        if (data?.profile_pic_id) {
          const profileData = await getSurgeonProfilePicture(surgeonId);
          data.profilePic = URL.createObjectURL(
            new Blob([new Uint8Array(profileData.data.file_content.data)], {
              type: profileData.data.mime_type,
            })
          );
        }
        setSurgeon(data);
        setPreferences(data.preferences || '');
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    if (open) {
      loadSurgeon();
    }
  }, [surgeonId, open]);

  useEffect(() => {
    if (editorRef) {
      editorRef.innerHTML = surgeon?.preferences || '';
    }
  }, [editorRef, surgeon?.preferences]);

  const handlePreferencesChange = (e: React.FormEvent<HTMLDivElement>) => {
    setPreferences(e.currentTarget.innerHTML);
  };

  const handleFormatText = (command: string) => {
    const selection = window.getSelection();
    const range = selection ? selection.getRangeAt(0) : null;
    if (command === 'uppercase' || command === 'lowercase') {
      if (selection && selection.rangeCount > 0 && range) {
        const text = range.toString();
        const newText =
          command === 'uppercase' ? text.toUpperCase() : text.toLowerCase();
        range.deleteContents();
        range.insertNode(document.createTextNode(newText));
      }
    } else if (command === 'insertUnorderedList' && range) {
      const span = document.createElement('span');
      const listElement = document.createElement('ul');
      listElement.style.marginLeft = '1rem';
      const li = document.createElement('li');
      li.appendChild(range.extractContents());
      listElement.appendChild(li);
      range.insertNode(listElement);
      span.appendChild(range.extractContents());
      range.insertNode(span);
    } else {
      document.execCommand(command, false);
    }
  };

  function cleanRichText(pref: string) {
    const trimmed = pref.trim();
    // Common "empty" placeholders
    if (
      trimmed === '<br>' ||
      trimmed === '<p><br></p>' ||
      trimmed === '<div><br></div>' ||
      trimmed === ''
    ) {
      return '';
    }
    return pref;
  }

  const handleSave = async () => {
    try {
      // Validate profile pic size
      if (surgeon?.profilePicFile) {
        // Check if file size is greater than 2 MB, throw error
        if (surgeon.profilePicFile.size > 2 * 1024 * 1024) {
          dispatch(
            _snackbarAcknowledgement(
              true,
              t('SURGEONS.EDIT_SURGEON.PROFILE_PIC_SIZE_EXCEED'),
              t('error')
            )
          );
          return;
        }
        const formData = new FormData();
        formData.append('profilePic', surgeon.profilePicFile); // Append file
        // Call api to update profile pic
        await updateSurgeonProfilePic(surgeonId, formData);
      }
      // Call api to update surgeon preferences
      await updateSurgeonPreferences(
        surgeonId,
        cleanRichText(preferences),
        deleteProfilePicture
      );

      if (onSave) onSave();
      dispatch(
        _snackbarAcknowledgement(
          true,
          t('SURGEONS.EDIT_SURGEON.PREFERENCES_UPDATED'),
          t('COMMON.SUCCESS')
        )
      );
      onClose();
    } catch (error) {
      const err = error as any;
      dispatch(_snackbarAcknowledgement(true, err.message, 'error'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSurgeon(surgeonId);
      dispatch(
        _snackbarAcknowledgement(
          true,
          t('SURGEONS.EDIT_SURGEON.SURGEON_DELETED'),
          t('COMMON.SUCCESS')
        )
      );
      if (onSave) onSave();
      setShowDeleteConfirmation(false);
      onClose(true);
    } catch (error) {
      const err = error as any;
      dispatch(_snackbarAcknowledgement(true, err.message, 'error'));
    }
  };

  const handleProfilePicUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = () => {
        const imageUrl = reader.result as string;
        setSurgeon(prev => {
          if (!prev) return null;
          return {
            ...prev,
            profilePic: imageUrl,
            profilePicFile: file, // Store file to send to API
          };
        });
      };

      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setSurgeon(prev => {
        if (!prev) return null;
        setDeleteProfilePicture(true);
        return {
          ...prev,
          profilePic: '',
          profilePicFile: null,
        };
      });
    } catch (error) {
      const err = error as any;
      dispatch(_snackbarAcknowledgement(true, err.message, 'error'));
    } finally {
      // reset the value to false
      setDeleteProfilePicture(false);
    }
  };

  if (!open || isLoading) return null;

  return (
    <div className="edit-surgeon-overlay">
      <div className="edit-surgeon-dialog">
        <div className="surgeon-profile">
          <div className="profile-pic-container">
            {surgeon?.profilePic ? (
              <div className="profile-pic-wrapper">
                <img
                  src={surgeon.profilePic}
                  alt="Profile"
                  className="profile-pic"
                  style={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
                <button
                  className="remove-photo-button"
                  onClick={handleRemovePhoto}
                  title={t('SURGEONS.EDIT_SURGEON.REMOVE_PHOTO')}>
                  <CloseIcon />
                </button>
              </div>
            ) : (
              <div className="default-profile-pic">
                <PersonIcon style={{fontSize: 48}} />
              </div>
            )}
            <input
              type="file"
              id="profile-upload"
              className="profile-upload"
              accept="image/png, image/jpg, image/jpeg"
              onChange={handleProfilePicUpload}
            />
            <label htmlFor="profile-upload" className="upload-label">
              {surgeon?.profilePic
                ? t('SURGEONS.EDIT_SURGEON.CHANGE_PHOTO')
                : t('SURGEONS.EDIT_SURGEON.UPLOAD_PHOTO')}
            </label>
          </div>

          <div className="surgeon-fields">
            <div className="edit-surgeon-header">
              <span className="edit-surgeon-title">
                {t('SURGEONS.EDIT_SURGEON.TITLE')}
              </span>
              <button
                className="close-button"
                onClick={() => {
                  onClose();
                }}>
                {t('SURGEONS.EDIT_SURGEON.CLOSE')}
              </button>
            </div>

            <div className="field-group">
              <span className="field-label">
                {t('SURGEONS.EDIT_SURGEON.FULL_NAME')}
              </span>
              <input
                type="text"
                className="field-input"
                value={surgeon?.name}
                disabled
              />
            </div>
            <div className="field-group">
              <span className="field-label">
                {t('SURGEONS.EDIT_SURGEON.SERVICE_LINE')}
              </span>
              <input
                type="text"
                className="field-input"
                value={surgeon?.service_line_name}
                disabled
              />
            </div>
            <div className="field-group">
              <span className="field-label">
                {t('SURGEONS.EDIT_SURGEON.SURGEON_ID')}
              </span>
              <input
                type="text"
                className="field-input"
                value={surgeon?.surgeon_id}
                disabled
              />
            </div>
          </div>
        </div>

        <div className="preferences-container">
          <div className="preferences-header">
            <label className="preferences-label">
              {t('SURGEONS.EDIT_SURGEON.PREFERENCES')}
            </label>
            <div className="rich-text-toolbar">
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('bold')}
                title="Bold">
                <FormatBoldIcon />
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('italic')}
                title="Italic">
                <FormatItalicIcon />
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('underline')}
                title="Underline">
                <FormatUnderlinedIcon />
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('uppercase')}
                title="Uppercase">
                <span style={{fontSize: '16px', fontWeight: 'bold'}}>AB</span>
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('lowercase')}
                title="Lowercase">
                <span style={{fontSize: '16px'}}>ab</span>
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('insertUnorderedList')}
                title="Bullet List">
                <FormatListBulletedIcon />
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => handleFormatText('insertOrderedList')}
                title="Numbered List">
                <FormatListNumberedIcon />
              </button>
            </div>
          </div>
          {surgeon && (
            <div
              ref={setEditorRef}
              className="preferences-editor"
              contentEditable
              onInput={handlePreferencesChange}
              suppressContentEditableWarning
            />
          )}
        </div>

        <div className="edit-surgeon-footer">
          <button
            className="delete-button"
            onClick={() => setShowDeleteConfirmation(true)}
            title="Delete Surgeon">
            <img alt="Delete" src={DeleteIcon} />
          </button>
          <button className="save-button" onClick={handleSave}>
            {t('SURGEONS.EDIT_SURGEON.SAVE')}
          </button>
        </div>
      </div>

      {showDeleteConfirmation && (
        <div className="delete-confirmation-dialog">
          <div className="confirmation-content">
            <h3>{t('SURGEONS.EDIT_SURGEON.DELETE_SURGEON')}</h3>
            <p>{t('SURGEONS.EDIT_SURGEON.DELETE_CONFIRMATION_TEXT')}</p>
            <div className="confirmation-buttons">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteConfirmation(false)}>
                {t('SURGEONS.EDIT_SURGEON.CANCEL')}
              </button>
              <button className="confirm-delete-button" onClick={handleDelete}>
                {t('SURGEONS.EDIT_SURGEON.DELETE')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditSurgeon;